import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { migrationApi, type AnnexPatch, type BlockPatch, type PlanPatch } from '@/api/clients/migrationApi'
import { discoveryApi } from '@/api/clients/discoveryApi'
import type * as discovery from '@/api/types/discovery'
import type { AnnexAsset, ExportFormat, MigrationPlan, MigrationPlanVersion, PlanChecks, PlanEvent, SubmissionMarked, SystemAnnex } from '@/api/types/migration'

// ── The submission plan from qtz-readiness ──────────────────────────────────
// The plan, its annexes (with their blocks), the review checks, versions, the
// submission record and the event log are read from the backend and written
// back through it; nothing about the plan lives in the browser any more. Text
// fields are typed into a local draft that is sent 600 ms after the last
// keystroke (or on blur) so a PUT is not made per character; discrete fields
// (options, radios, adds and removes) are sent at once. Every write then
// re-reads the plan queries, so checks and completeness follow the backend.

export const planKeys = {
  root: (orgId: string, indexId: string) => ['migration-plan', orgId, indexId] as const,
  plan: (orgId: string, indexId: string) => ['migration-plan', orgId, indexId, 'plan'] as const,
  annexes: (orgId: string, indexId: string) => ['migration-plan', orgId, indexId, 'annexes'] as const,
  checks: (orgId: string, indexId: string) => ['migration-plan', orgId, indexId, 'checks'] as const,
  versions: (orgId: string, indexId: string) => ['migration-plan', orgId, indexId, 'versions'] as const,
  submissions: (orgId: string, indexId: string) => ['migration-plan', orgId, indexId, 'submissions'] as const,
  events: (orgId: string, indexId: string) => ['migration-plan', orgId, indexId, 'events'] as const,
}
export const systemsKey = (orgId: string) => ['discovery-systems', orgId] as const

// The Discovery facts the plan screens need about a system: the CSA scope
// (cii), its name and size, and its last scan.
export type PlanSystem = Pick<discovery.System, 'id' | 'name' | 'asset_count' | 'cii' | 'last_scan_at'>

export interface PlanData {
  plan: MigrationPlan | null
  annexes: SystemAnnex[] // in rail order, no_longer_cii ones included
  checks: PlanChecks | null
  versions: MigrationPlanVersion[]
  submissions: SubmissionMarked[]
  events: PlanEvent[] // newest first
  systems: PlanSystem[]
  loading: boolean
  error: Error | null
}

// A submitted version is read-only through every route (§7.2).
export const isPlanLocked = (plan: MigrationPlan | null | undefined) => plan?.plan_state?.status === 'submitted'

// isPlan recognises a plan the backend served: an object with its version state.
const isPlan = (v: unknown): v is MigrationPlan => !!v && typeof v === 'object' && !Array.isArray(v) && typeof (v as MigrationPlan).plan_state?.n === 'number'

// ── Drafts: what the person typed and the backend has not confirmed yet ──────

interface DraftState {
  drafts: Record<string, unknown>
  set: (id: string, value: unknown) => void
  clear: (ids: string[]) => void
}

const useDraftStore = create<DraftState>()((set) => ({
  drafts: {},
  set: (id, value) => set((s) => ({ drafts: { ...s.drafts, [id]: value } })),
  clear: (ids) =>
    set((s) => {
      const next = { ...s.drafts }
      ids.forEach((id) => delete next[id])
      return { drafts: next }
    }),
}))

const planDraftId = (orgId: string, indexId: string, key: string) => `plan:${orgId}:${indexId}:${key}`
const annexDraftId = (annexId: string, key: string) => `annex:${annexId}:${key}`
const blockDraftId = (blockId: string, key: string) => `block:${blockId}:${key}`

function withDrafts<T extends object>(row: T, prefix: string, drafts: Record<string, unknown>): T {
  const patch = Object.entries(drafts).filter(([id]) => id.startsWith(prefix)).map(([id, v]) => [id.slice(prefix.length), v] as const)
  return patch.length ? { ...row, ...Object.fromEntries(patch) } : row
}

// ── Debounced writes ────────────────────────────────────────────────────────

const TEXT_DEBOUNCE_MS = 600
const pending = new Map<string, { timer: number; run: () => Promise<void> }>()

function schedule(id: string, run: () => Promise<void>) {
  const cur = pending.get(id)
  if (cur) window.clearTimeout(cur.timer)
  const timer = window.setTimeout(() => {
    pending.delete(id)
    void run()
  }, TEXT_DEBOUNCE_MS)
  pending.set(id, { timer, run })
}

// flushDrafts sends every pending text edit now (on blur, before a save).
export async function flushDrafts(): Promise<void> {
  const runs = [...pending.values()]
  pending.forEach((p) => window.clearTimeout(p.timer))
  pending.clear()
  await Promise.all(runs.map((p) => p.run()))
}

// ── Reads ───────────────────────────────────────────────────────────────────

export function usePlanData(orgId: string, indexId: string, enabled: boolean): PlanData {
  const on = enabled && !!orgId && !!indexId
  const plan = useQuery({ queryKey: planKeys.plan(orgId, indexId), queryFn: () => migrationApi.plan(orgId, indexId), enabled: on })
  const annexes = useQuery({ queryKey: planKeys.annexes(orgId, indexId), queryFn: () => migrationApi.annexes(orgId, indexId), enabled: on })
  const checks = useQuery({ queryKey: planKeys.checks(orgId, indexId), queryFn: () => migrationApi.checks(orgId, indexId), enabled: on })
  const versions = useQuery({ queryKey: planKeys.versions(orgId, indexId), queryFn: () => migrationApi.versions(orgId, indexId), enabled: on })
  const submissions = useQuery({ queryKey: planKeys.submissions(orgId, indexId), queryFn: () => migrationApi.submissions(orgId, indexId), enabled: on })
  const events = useQuery({ queryKey: planKeys.events(orgId, indexId), queryFn: () => migrationApi.events(orgId, indexId), enabled: on })
  const systems = useQuery({ queryKey: systemsKey(orgId), queryFn: () => discoveryApi.systems(orgId), enabled: on })
  const drafts = useDraftStore((s) => s.drafts)

  // A backend that does not serve the plan (or a mock answering with an empty
  // list) must read as "no plan", not as a plan with no state.
  const served = isPlan(plan.data) ? plan.data : null
  const planWithDrafts = useMemo(() => (served ? withDrafts(served, `plan:${orgId}:${indexId}:`, drafts) : null), [served, drafts, orgId, indexId])
  const annexesWithDrafts = useMemo(
    () =>
      [...(annexes.data ?? [])]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((a) => ({ ...withDrafts(a, `annex:${a.id}:`, drafts), blocks: [...(a.blocks ?? [])].sort((x, y) => x.sort_order - y.sort_order).map((b) => withDrafts(b, `block:${b.id}:`, drafts)) })),
    [annexes.data, drafts]
  )
  const error = [plan, annexes, checks, versions, submissions, events].map((q) => q.error).find((e): e is Error => !!e) ?? null
  return {
    plan: planWithDrafts,
    annexes: annexesWithDrafts,
    checks: checks.data && Array.isArray(checks.data.blocking) ? checks.data : null,
    versions: versions.data ?? [],
    submissions: submissions.data ?? [],
    events: events.data ?? [],
    systems: (systems.data ?? []).filter((s) => !!s?.id).map((s) => ({ id: s.id, name: s.name, asset_count: s.asset_count, cii: !!s.cii, last_scan_at: s.last_scan_at })),
    // Versions count too: the print view redirects when the plan has loaded
    // and the version it was asked for is not there yet.
    loading: on && (plan.isPending || annexes.isPending || versions.isPending),
    error,
  }
}

// ── Writes ──────────────────────────────────────────────────────────────────

export function usePlanActions(orgId: string, indexId: string, actor: string) {
  const qc = useQueryClient()
  const setDraft = useDraftStore((s) => s.set)
  const clearDrafts = useDraftStore((s) => s.clear)

  const invalidate = useCallback(() => qc.invalidateQueries({ queryKey: planKeys.root(orgId, indexId) }), [qc, orgId, indexId])

  // write runs a backend call, then re-reads the plan whatever happened: a
  // refused write (locked plan, blocking checks) must not leave stale data.
  const write = useCallback(
    async <T,>(fn: () => Promise<T>, draftIds: string[] = []): Promise<T> => {
      try {
        return await fn()
      } finally {
        await invalidate()
        if (draftIds.length) clearDrafts(draftIds)
      }
    },
    [invalidate, clearDrafts]
  )

  // typed sets a draft at once and sends the field after the debounce.
  const typed = useCallback(
    (draftId: string, send: () => Promise<unknown>) => {
      schedule(draftId, () => write(send, [draftId]).then(() => undefined, () => undefined))
    },
    [write]
  )

  return useMemo(
    () => ({
      flush: flushDrafts,

      // Part A and header. Text keys debounce; everything else goes at once.
      updatePlanField: (key: string, value: unknown) => {
        const id = planDraftId(orgId, indexId, key)
        setDraft(id, value)
        const send = () => migrationApi.updatePlan(orgId, indexId, { [key]: value, actor } as PlanPatch)
        if (typeof value === 'string') typed(id, send)
        else void write(send, [id])
      },
      revertPlanField: (key: string) => write(() => migrationApi.updatePlan(orgId, indexId, { revert: [key], actor })),
      setSubmissionDate: (date: string | null) => write(() => migrationApi.updatePlan(orgId, indexId, { submission_date: date, actor })),

      // Annexes
      addAnnexFromSystem: (systemId: string) => write(() => migrationApi.createAnnex(orgId, indexId, { system_id: systemId, actor })),
      addBlankAnnex: () => write(() => migrationApi.createAnnex(orgId, indexId, { system_id: null, actor })),
      duplicateAnnex: (annexId: string) => write(() => migrationApi.createAnnex(orgId, indexId, { system_id: null, duplicate_of: annexId, actor })),
      removeAnnex: (annexId: string) => write(() => migrationApi.deleteAnnex(orgId, indexId, annexId, { actor })),
      archiveAnnex: (annexId: string, reason: string) => write(() => migrationApi.archiveAnnex(orgId, indexId, annexId, { reason, actor })),
      restoreAnnex: (annexId: string) => write(() => migrationApi.restoreAnnex(orgId, indexId, annexId, { actor })),
      reorderAnnexes: (orderedIds: string[]) => {
        // Optimistic: the rail reorders at once, the backend confirms.
        qc.setQueryData<SystemAnnex[]>(planKeys.annexes(orgId, indexId), (cur) => cur?.map((a) => ({ ...a, sort_order: orderedIds.indexOf(a.id) })))
        return write(() => migrationApi.reorderAnnexes(orgId, indexId, { ordered_ids: orderedIds, actor }))
      },
      updateAnnexField: (annexId: string, key: keyof AnnexPatch & string, value: string) => {
        const id = annexDraftId(annexId, key)
        setDraft(id, value)
        typed(id, () => migrationApi.updateAnnex(orgId, indexId, annexId, { [key]: value, actor } as AnnexPatch))
      },
      revertAnnexField: (annexId: string, key: string) => write(() => migrationApi.updateAnnex(orgId, indexId, annexId, { revert: [key], actor })),
      markAnnexComplete: (annexId: string, complete: boolean) => write(() => migrationApi.updateAnnex(orgId, indexId, annexId, { complete, actor })),

      // Section-2 blocks
      addBlockFromSource: (annexId: string, sourceId: string) => write(() => migrationApi.addAnnexAsset(orgId, indexId, annexId, { source_id: sourceId, actor })),
      addManualBlock: (annexId: string) => write(() => migrationApi.addAnnexAsset(orgId, indexId, annexId, { source_id: null, actor })),
      removeBlock: (annexId: string, blockId: string) => write(() => migrationApi.deleteAnnexAsset(orgId, indexId, annexId, blockId, { actor })),
      updateBlockField: (annexId: string, blockId: string, key: keyof BlockPatch & string, value: string | null) => {
        const id = blockDraftId(blockId, key)
        setDraft(id, value)
        const send = () => migrationApi.updateAnnexAsset(orgId, indexId, annexId, blockId, { [key]: value, actor } as BlockPatch)
        if (key === 'will_migrate') void write(send, [id])
        else typed(id, send)
      },
      revertBlockField: (annexId: string, blockId: string, key: string) => write(() => migrationApi.updateAnnexAsset(orgId, indexId, annexId, blockId, { revert: [key], actor })),

      // Sync, versions, submission
      sync: () => write(() => migrationApi.sync(orgId, indexId, { actor })),
      saveVersion: async (input: { label: string; submission_date: string; note: string | null; export: ExportFormat[] }) => {
        await flushDrafts()
        return write(() => migrationApi.saveVersion(orgId, indexId, { ...input, actor }))
      },
      markSubmitted: async (input: Omit<SubmissionMarked, 'id' | 'version' | 'recorded_at' | 'ip' | 'final_pdf_version_id'> & { auth_time: string }) => {
        await flushDrafts()
        return write(() => migrationApi.markSubmitted(orgId, indexId, { ...input, actor }))
      },
      startNewVersion: () => write(() => migrationApi.startNewVersion(orgId, indexId, { actor })),
    }),
    [orgId, indexId, actor, qc, setDraft, typed, write]
  )
}

export type PlanActions = ReturnType<typeof usePlanActions>

// blocksOf is the annex's blocks in order; kept as a function so callers read
// the same way they did when blocks lived in a separate list.
export const blocksOf = (annex: SystemAnnex): AnnexAsset[] => annex.blocks ?? []
